import React from 'react';
import { remove } from 'api/TeamController';
import { LayoutContext } from 'contexts';
import errorReporter from '@app/errorReporter';
import {
  Text,
  Dropdown,
  Menu,
  MenuItem,
  Button,
  Icon,
  message,
  TableProps,
  Space,
} from 'components';
import { useHistory } from 'react-router-dom';
import { StyledTable } from '../../../components';
import { addDatacy, addDatacyToHeader } from '../util';
import ManageUsersContext from '../ManageUsersContext';
import ManageUsersPagination from './ManageUsersPagination';
import { StyledSizeText } from './StyledComponents';
import { TEAM_ADMIN_DELETE_MSG } from './constants';
import { TeamGroupModal } from '../TeamGroupModal';
import { TeamGroupModalContext } from '../TeamGroupModal/TeamGroupModalContext';
import TeamGroupRemovalConfirmation from './TeamGroupRemovalConfirmation';

const DELETE_MSG = 'Are you sure you’d like to delete this team?';

const TeamsList: React.FC = () => {
  const history = useHistory();
  const { transition } = React.useContext(LayoutContext);
  const {
    isTeamsLoading: isLoading,
    fetchTeams,
    teams,
  } = React.useContext(ManageUsersContext);
  const { openModal } = React.useContext(TeamGroupModalContext);
  const { setTeamId } = React.useContext(ManageUsersContext);

  const [popconfirmVisibility, setPopconfirmVisibility] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleDeleteCancel = () => setPopconfirmVisibility(false);

  const handleDelete = React.useCallback(
    async ({
      id,
      name,
      teamAdminCount,
    }: {
      id: string;
      name: string;
      teamAdminCount: number;
    }) => {
      if (teamAdminCount > 0) {
        handleDeleteCancel();
        return;
      }

      try {
        setIsDeleting(true);
        await remove(id);
        message.success(`${name} is now deleted`);
      } catch (err) {
        let msg = err.message;
        if (err.isAxiosError && err.response?.data?.message) {
          msg = err.response.data.message;
        }
        errorReporter.error(`Failed to remove team: ${msg}`);
        message.error(`Failed to remove team: ${msg}`);
      } finally {
        setIsDeleting(false);
        await fetchTeams();
      }
    },
    [],
  );

  const dataSource = React.useMemo(() => {
    const data = teams.map(team => ({
      id: team.id,
      name: team.name,
      userCount: team.userCount,
      teamAdminCount: team.teamAdminCount,
      key: team.id,
      teamMlsId: team.teamMlsId,
    }));
    return data;
  }, [teams]);

  React.useEffect(() => {
    const searchParams = new URLSearchParams(history.location.search);
    const teamIdToDelete = searchParams.get('deleteTeamId');
    if (teamIdToDelete) {
      const { id, name, teamAdminCount } =
        teams.find(team => team.id === teamIdToDelete) || {};
      if (id) {
        handleDelete({ id, name, teamAdminCount });
        searchParams.delete('deleteTeamId');
        history.replace({ search: searchParams.toString() });
      }
    }
  }, [handleDelete, history, teams]);

  const columns: TableProps['columns'] = [
    {
      title: 'Team Name',
      key: 'teamName',
      dataIndex: 'name',
      sorter: (x, y) => {
        const a = x.name.toUpperCase();
        const b = y.name.toUpperCase();

        if (a === b) {
          return 0;
        }
        if (a > b) {
          return 1;
        }
        return -1;
      },
      render: (name, team) => (
          <Space key={team.id} wrap>
            <Text nowrap ellipsis={name.length > 30 ? 300 : false}>
              {name}
            </Text>
            <StyledSizeText>
              {team.userCount} Member{team.userCount !== 1 ? 's' : ''}
            </StyledSizeText>
          </Space>
        ),
      onCell: addDatacy('teams-name'),
      onHeaderCell: addDatacyToHeader('teams-name'),
    },
    {
      width: '40%',
      title: '',
      key: 'teamId',
      dataIndex: 'teamMlsId',
      render: (teamMlsId, team) => (
          <Space key={team.id}>
            <StyledSizeText>
              {teamMlsId ? `MLS ID: ${teamMlsId}` : ''}
            </StyledSizeText>
          </Space>
        ),
      onCell: addDatacy('teams-mls'),
      onHeaderCell: addDatacyToHeader('teams-mls'),
    },
    {
      width: '15%',
      title: '',
      key: 'actions',
      dataIndex: 'actions',
      align: 'center',
      render: (teamName, team) => {
        const { id, name, teamAdminCount } = team;
        return (
          <Dropdown
            placement="topLeft"
            trigger={['click']}
            dropdownRender={() => (
              <div onClick={e => e.stopPropagation()} data-cy="teams-dropdown">
                <TeamGroupRemovalConfirmation
                  title={
                    teamAdminCount > 0 ? TEAM_ADMIN_DELETE_MSG : DELETE_MSG
                  }
                  canDelete={teamAdminCount === 0}
                  visible={popconfirmVisibility}
                  onCancel={handleDeleteCancel}
                  handleDelete={() =>
                    handleDelete({ id, name, teamAdminCount })
                  }
                >
                  <Menu>
                    <MenuItem
                      key={'edit'}
                      onClick={() => {
                        openModal('team', 'edit', id);
                      }}
                      data-cy="teams-dropdown-edit"
                    >
                      Edit Team
                    </MenuItem>
                    <MenuItem
                      key={'delete'}
                      onClickCapture={e => {
                        setPopconfirmVisibility(true);
                        e.stopPropagation();
                      }}
                      data-cy="teams-dropdown-delete"
                    >
                      Delete Team
                    </MenuItem>
                  </Menu>
                </TeamGroupRemovalConfirmation>
              </div>
            )}
          >
            <Button
              type="text"
              size="small"
              onClick={e => {
                e.stopPropagation();
              }}
            >
              <Icon type="ellipsis" width={25} />
            </Button>
          </Dropdown>
        );
      },
      onCell: addDatacy('teams-options'),
    },
  ];

  const processedColumns = columns.filter(Boolean);

  return (
    <>
      <StyledTable
        style={{
          transition: `all ${transition}s`,
        }}
        pagination={false}
        showSorterTooltip={false}
        loading={isLoading || isDeleting}
        dataSource={dataSource}
        columns={processedColumns}
        scroll={{ x: '100%' }}
        tableLayout="fixed"
        onRow={team => ({
          onClick: () => {
            setTeamId(team.id);
            history.push(
              `/users/list/team/${team.id}${history.location.search}`,
            );
          },
        })}
        data-cy="teams-table"
      />
      <ManageUsersPagination hidden tableName="teams" />
      <TeamGroupModal destroyOnClose onSave={fetchTeams} />
    </>
  );
};

export default TeamsList;
